import { Inject, Injectable, InjectionToken } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { CommandInput, CommandTemplate } from '../../../entities/commandTemplate/commandTemplate';
import { CompletionBehaviorTemplate } from '../../../entities/completionBehaviorTemplate/completionBehaviorTemplate';
import { ApolloService } from '../../apollo.service';
import * as fromGraphQl from './graphql';

export interface Flow {
  executeFlow(commandService: CommandService, hasCommands: HasCommands,
    commandTemplate: CommandTemplate, commandInput: CommandInput, completionCallback: () => void): any;
}

export interface CompletionBehavior {
  executeCompletionBehavior(hasCommands: HasCommands, CompletionBehaviorTemplate: CompletionBehaviorTemplate, reloadable?: Reloadable): any;
}

export interface Reloadable {
  reload(): void;
}

export const FLOW_MAP = new InjectionToken<Map<string, Flow>>('FLOW_MAP');
export const ADDITIONAL_FLOWS = new InjectionToken<Map<string, Flow>>('ADDITIONAL_FLOWS');
export const COMPLETION_BEHAVIOR_MAP = new InjectionToken<Map<string, CompletionBehavior>>('COMPLETION_BEHAVIOR_MAP');
export const ADDITIONAL_COMPLETION_BEHAVIORS = new InjectionToken<Map<string, CompletionBehavior>>('ADDITIONAL_COMPLETION_BEHAVIORS');

/**
 * Class executes commands that were predefined by the server.
 */
@Injectable({
  providedIn: 'root'
})
export class CommandService {

  constructor(
    /**
     * ApolloService - service for executing graphql queries
     */
    private apolloService: ApolloService,
    /**
     * MatDialog - service for opening dialogs
     */
    public dialog: MatDialog,
    @Inject(FLOW_MAP) private strategies: Map<string, Flow>,
    @Inject(COMPLETION_BEHAVIOR_MAP) private completionBehaviors: Map<string, CompletionBehavior>,
  ) { }

  /**
   * Executes a command - main entry point for executing commands
   * @param commands - the calling class that implements the commands interface
   * @param commandTemplate - the command that should be executed
   * */
  public execute(hasCommands: HasCommands, commandTemplate: CommandTemplate, reloadable?: Reloadable) {

    let flowName;
    // if we have "customFlow" we have to look 
    if(commandTemplate.flow?.name === "customFlow" && commandTemplate.flow?.settings?.customFlowName){
      flowName = commandTemplate.flow?.settings?.customFlowName;
    } else {
      flowName = commandTemplate.flow?.name;
    }

    // get the flow from the map
    const flow = this.strategies.get(flowName);

    // prepare an command input
    let commandInput = new CommandInput();
    commandInput.name = commandTemplate.name;
    // check, if we have an parameterObject with predefined values
    if(commandTemplate.parameters){
      commandTemplate.parameters.map( (parameter) => {
        // add all parameters that already have a value from the backend
        if(parameter.value){
          commandInput.addParameter(parameter.name, parameter.value);
        }
      });
    }

    // build the completion behavior
    let completionBehaviorName;
    if(commandTemplate.flow.completionBehavior?.name === "customCompletionBehavior" && commandTemplate.flow.completionBehavior?.settings?.customCompletionBehaviorName){
      completionBehaviorName = commandTemplate?.flow.completionBehavior?.settings?.customCompletionBehaviorName;
    } else {
      completionBehaviorName = commandTemplate?.flow?.completionBehavior?.name;
    }
    const completionBehavior = this.completionBehaviors.get(completionBehaviorName);

    flow?.executeFlow(this, hasCommands, commandTemplate, commandInput, () => {
      if(completionBehavior) {
        completionBehavior.executeCompletionBehavior(hasCommands, commandTemplate.flow?.completionBehavior, reloadable);
      }
    });

  }

  public sendCommand(commandInput: CommandInput, completionBehavior: any) {
    this.apolloService.mutateAndExecute(
      fromGraphQl.EXECUTE_COMMAND_MUTATION,
      {
        commandInput,
      },
      // call the completion behavior
      completionBehavior
    );
  }
}

/**
 * Interface that has to be implemented by the class that calls the command service
 */
export interface HasCommands {

  getEntityId(): string;

  getCollectionRoute(): string;

}